import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Sparkles, X, AlertTriangle, RefreshCw } from "lucide-react";
import { api } from "../lib/api";
import useEscClose from "../hooks/useEscClose";

/**
 * "Explain this lab" modal — opens a Claude Sonnet 4.5 powered coach summary
 * for the supplied lab deviations.
 *
 * Props:
 *   open        : bool
 *   onClose     : fn()
 *   sailorId    : string
 *   sailorName  : string (header label)
 *   recordId    : string | null   — used as cache key so repeat opens are free
 *   date        : string | null   — lab date label
 *   deviations  : [{key,label,value,unit,status,range_low,range_high}]
 *                 Pre-computed by the parent so this modal stays presentational.
 *   contextLabel: short subtitle (e.g. "15 Apr 2026 · 4 markers out of range")
 */
export default function ExplainModal({
  open,
  onClose,
  sailorId,
  sailorName,
  recordId,
  date,
  deviations,
  contextLabel,
}) {
  const [text, setText] = useState("");
  const [cached, setCached] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEscClose(open, onClose);

  const fetchExplanation = async () => {
    setLoading(true);
    setError("");
    setText("");
    try {
      const r = await api.post("/explain/lab-deviations", {
        sailor_id: sailorId,
        record_id: recordId,
        date: date,
        deviations,
      });
      setText(r.data?.text || "");
      setCached(!!r.data?.cached);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Something went wrong.";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    if (!sailorId || !deviations || deviations.length === 0) return;
    fetchExplanation();
    // We deliberately re-run only when the modal opens or the input set
    // changes; the cached flag on the backend prevents repeat billing.
  }, [open, sailorId, recordId, JSON.stringify(deviations || [])]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-8"
      onClick={onClose}
      data-testid="explain-modal-backdrop"
    >
      <div
        className="relative w-full max-w-2xl mx-4 bg-[#0B1627] border border-[#1E304B] rounded-xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="explain-modal"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-[#1E304B]">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-amber-300/90">
              <Sparkles size={12} /> Coach summary · Claude Sonnet 4.5
            </div>
            <div className="font-display text-lg text-slate-100 mt-1 truncate">
              {sailorName || "Athlete"}
            </div>
            {contextLabel && (
              <div className="text-xs text-slate-400 mt-0.5 truncate">{contextLabel}</div>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-100 p-1 rounded"
            data-testid="explain-modal-close"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 min-h-[180px] max-h-[60vh] overflow-y-auto">
          {loading && (
            <div className="flex items-center gap-2 text-slate-400 text-sm" data-testid="explain-loading">
              <RefreshCw size={14} className="animate-spin text-amber-300" />
              Thinking… Claude is reviewing {deviations?.length || 0} marker
              {(deviations?.length || 0) === 1 ? "" : "s"} for {sailorName}…
            </div>
          )}

          {!loading && error && (
            <div className="text-rose-300 text-sm flex items-start gap-2" data-testid="explain-error">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              <div>
                <div className="font-medium">Couldn&apos;t generate explanation</div>
                <div className="text-xs text-rose-300/80 mt-1">{error}</div>
                <button
                  onClick={fetchExplanation}
                  className="mt-2 inline-flex items-center gap-1 px-2 py-1 rounded border border-rose-500/40 text-rose-200 hover:bg-rose-500/15 text-xs"
                  data-testid="explain-retry"
                >
                  <RefreshCw size={11} /> Retry
                </button>
              </div>
            </div>
          )}

          {!loading && !error && text && (
            <div data-testid="explain-text">
              <ExplanationText text={text} />
              <div className="mt-4 text-[10px] text-slate-500 flex items-center gap-2">
                {cached && (
                  <span className="px-1.5 py-0.5 rounded border border-emerald-500/40 text-emerald-300 uppercase tracking-wider">
                    Cached
                  </span>
                )}
                <span>Generated by Anthropic Claude Sonnet 4.5 · for educational purposes only</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-[#1E304B] flex items-center justify-end gap-2 bg-[#0a1422]">
          {!loading && !error && text && (
            <button
              onClick={() => {
                navigator.clipboard?.writeText(text);
              }}
              className="px-3 py-1.5 rounded border border-slate-600 text-slate-200 hover:bg-slate-700/40 text-xs"
              data-testid="explain-copy"
            >
              Copy to clipboard
            </button>
          )}
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded bg-cyan-500/15 border border-cyan-500/50 text-cyan-200 hover:bg-cyan-500/25 text-xs"
            data-testid="explain-done"
          >
            Done
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Lightweight markdown renderer for the very narrow subset Claude returns:
 *   - **Bold** at start of a line is treated as a heading
 *   - Blank lines split paragraphs
 *   - Everything else is plain text
 * Keeps us off a full markdown dependency for one-paragraph blurbs.
 */
function ExplanationText({ text }) {
  const blocks = text.split(/\n{2,}/).map((b, i) => {
    // Use the first 60 chars of the block as a stable key — markdown blocks
    // don't reorder so this is unique enough for React's reconciliation.
    const key = `b-${i}-${b.slice(0, 60)}`;
    // Detect a leading bold "header" line followed by body lines
    const lines = b.split("\n");
    const firstLine = lines[0] || "";
    const boldMatch = firstLine.match(/^\*\*(.+?)\*\*$/);
    if (boldMatch) {
      return (
        <div key={key} className="mb-3">
          <div className="text-cyan-200 text-sm font-display font-semibold leading-snug">
            {boldMatch[1]}
          </div>
          <div className="text-slate-200 text-[13px] leading-relaxed mt-0.5 whitespace-pre-wrap">
            {lines.slice(1).join("\n")}
          </div>
        </div>
      );
    }
    // Fallback paragraph — inline-bold via simple split
    return (
      <p key={key} className="text-slate-200 text-[13px] leading-relaxed mb-3 whitespace-pre-wrap">
        {renderInlineBold(b)}
      </p>
    );
  });
  return <div>{blocks}</div>;
}

function renderInlineBold(s) {
  const parts = [];
  const re = /\*\*(.+?)\*\*/g;
  let lastIndex = 0;
  let m;
  let key = 0;
  while ((m = re.exec(s)) !== null) {
    if (m.index > lastIndex) parts.push(s.slice(lastIndex, m.index));
    parts.push(<strong key={key++} className="text-cyan-200">{m[1]}</strong>);
    lastIndex = re.lastIndex;
  }
  if (lastIndex < s.length) parts.push(s.slice(lastIndex));
  return parts;
}
